import { commonStyles } from 'features/cart/styles';
import { ICartEntry, MinimalCartEntryType } from 'features/cart/types';
import {
  calculateEntryChildrenPrice,
  isIncentiveOrCombo,
  isRewardCartEntry,
} from 'features/cart/utils';
import { StyleSheet, Text, View } from 'react-native';

import Picture from 'components/picture';
import { t } from 'state/intl/translations';
import { useUIContext } from 'state/ui';
import { primitive } from 'styles/constants/primitives';

import { CartEntryFabButton } from '../cart-actions/cart-entry-fab-button';

const ModifiersNames = ({ modifiersNames }: { modifiersNames: string }) => {
  if (!modifiersNames) {
    return null;
  }
  return (
    <View style={styles.modifiersContainer}>
      <Text style={commonStyles.cartEntryItemName}>{t('yourWay') + ' '}</Text>
      <Text style={[commonStyles.cartEntryBody, { flexShrink: 1 }]}>{modifiersNames}</Text>
    </View>
  );
};

const CartItem = ({
  cartEntry,
  isSingleItem = false,
  showFabButton = true,
}: {
  cartEntry: ICartEntry;
  isSingleItem?: boolean;
  showFabButton?: boolean;
}) => {
  const { formatPriceWithSign } = useUIContext();
  const modifiersNames = cartEntry.children.map(child => child.name).join(', ');
  const modifiersPrice = calculateEntryChildrenPrice(cartEntry);
  const price = isSingleItem ? modifiersPrice : modifiersPrice + (cartEntry.price?.amount ?? 0);
  const priceLabel = price > 0 ? formatPriceWithSign(price) : '';
  if (cartEntry.type !== MinimalCartEntryType.item) {
    return null;
  }

  return (
    <View style={styles.item}>
      <Picture
        size="$12"
        alt={cartEntry.image.locale?.imageDescription || ''}
        image={cartEntry.image}
      />
      <View style={styles.itemDetails}>
        <Text numberOfLines={2} ellipsizeMode="tail" style={commonStyles.cartEntryItemName}>
          {cartEntry.name}
        </Text>
        {priceLabel && <Text style={commonStyles.cartEntryBody}>{priceLabel}</Text>}
        <ModifiersNames modifiersNames={modifiersNames} />
      </View>
      {showFabButton && <CartEntryFabButton cartEntryId={cartEntry.cartId} />}
    </View>
  );
};

const ComboItem = ({ cartEntry }: { cartEntry: ICartEntry }) => {
  return cartEntry.children.map(child => (
    <CartItem key={child.cartId ?? child.id} cartEntry={child} showFabButton={false} />
  ));
};

const RewardItem = ({ cartEntry }: { cartEntry: ICartEntry }) => {
  const rewardBenefit = cartEntry.children?.[0];
  if (!rewardBenefit) {
    return null;
  }
  const isItem = rewardBenefit.type === MinimalCartEntryType.item;
  if (!isItem) {
    return <ComboItem cartEntry={rewardBenefit} />;
  }
  return <CartItem cartEntry={rewardBenefit} isSingleItem={isItem} />;
};

const CartEntry = ({ cartEntry }: { cartEntry: ICartEntry }) => {
  if (isRewardCartEntry(cartEntry)) {
    return <RewardItem cartEntry={cartEntry} />;
  }
  if (isIncentiveOrCombo(cartEntry)) {
    return <ComboItem cartEntry={cartEntry} />;
  }
  return <CartItem cartEntry={cartEntry} isSingleItem />;
};

export const CartEntryBody = ({ cartEntry }: { cartEntry: ICartEntry }) => {
  return (
    <View style={{ flexDirection: 'column' }}>
      <CartEntry cartEntry={cartEntry} />
    </View>
  );
};

const styles = StyleSheet.create({
  modifiersContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  item: {
    flexDirection: 'row',
    gap: 12,
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: primitive.$blackOpacity10,
  },
  itemDetails: {
    flexDirection: 'column',
    gap: 4,
    flex: 1,
    minWidth: 0,
  },
});
